import {
  formatClock as renderClock,
  formatDatePattern,
  type ClockParts,
  type DatePatternParts,
  type Locale,
  type TimeFormat,
} from '@aysar/contracts';

import { browserTimeZone, wallClockOf } from '@/shared/utils/local-instant/local-instant.util';
import { meridiemFor, patternNamesFor } from '@/shared/utils/pattern-names/pattern-names.util';

import { numberLocale } from '../format-number/format-number.util';

/** Shown instead of `Invalid Date` — an instant nobody recorded is not news. */
export const NO_INSTANT = '—';

/** What every date and clock is rendered with — `brand.dateFormat` / `brand.timeFormat`, via `useBrand()`. */
export interface DateTimeFormats {
  dateFormat: string;
  timeFormat: TimeFormat;
}

const cache = new Map<string, Intl.DateTimeFormat>();

/**
 * A memoized `Intl.DateTimeFormat` for one locale and option set.
 *
 * Built over {@link numberLocale}, so the Arabic `-u-nu-latn` digit pin lives in
 * exactly **one** place rather than being re-derived at each call site.
 *
 * Since P28 this is NOT how a date or a clock is rendered — those follow the
 * brand's `dateFormat` / `timeFormat` below. It stays for the one caller class a
 * brand pattern cannot serve: a chart's axis tick, which is a density-constrained
 * annotation (`chart-tick.util`) rather than a date anyone reads as one.
 */
export function dateTimeFormatter(
  locale: Locale,
  options: Intl.DateTimeFormatOptions,
): Intl.DateTimeFormat {
  const key = `${locale}|${JSON.stringify(options)}`;
  const cached = cache.get(key);
  if (cached !== undefined) return cached;

  const format = new Intl.DateTimeFormat(numberLocale(locale), options);
  cache.set(key, format);
  return format;
}

/** The pause between a day and its clock; the Arabic comma is a different glyph. */
const JOINER: Readonly<Record<Locale, string>> = { ar: '، ', en: ', ' };

/** Between the two ends of a range — spaced, so it reads the same in both directions. */
const RANGE_DASH = ' – ';

/** A bare calendar day (`YYYY-MM-DD`) — a date of birth, a day key — with no zone to read it in. */
const CALENDAR_DAY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/u;

/**
 * The parts a value renders from. A bare `YYYY-MM-DD` is the day it names and
 * is never passed through a zone — that would move a birth date a day west of
 * Greenwich; an instant is read on the wall clock of `timeZone`.
 */
function partsOf(
  value: string | null | undefined,
  timeZone: string,
): (DatePatternParts & ClockParts) | null {
  if (value === null || value === undefined) return null;

  const day = CALENDAR_DAY_PATTERN.exec(value);
  if (day !== null) {
    const [, year = '', month = '', date = ''] = day;
    const weekday = new Date(Date.UTC(Number(year), Number(month) - 1, Number(date))).getUTCDay();
    return {
      year: Number(year),
      month: Number(month),
      day: Number(date),
      weekday,
      hour: 0,
      minute: 0,
    };
  }

  return wallClockOf(value, timeZone);
}

/** Whether the pattern itself names the weekday (outside a `[…]` literal). */
function namesWeekday(pattern: string): boolean {
  return /d{3,4}/u.test(pattern.replace(/\[[^\]]*\]/gu, ''));
}

/**
 * A calendar day — a join date, a date of birth — under `brand.dateFormat` (P28).
 *
 * Rendered by the same `formatDatePattern` the settings preview uses (U3), with
 * this locale's month and weekday names. `formats` comes from `useBrand()`; a
 * caller mounted before the payload lands renders the contracts default and
 * re-renders once with the brand's.
 */
export function formatDate(
  value: string | null | undefined,
  locale: Locale,
  formats: DateTimeFormats,
  timeZone: string = browserTimeZone(),
): string {
  const parts = partsOf(value, timeZone);
  if (parts === null) return NO_INSTANT;

  return formatDatePattern(formats.dateFormat, parts, patternNamesFor(locale));
}

/**
 * A day as a HEADING — a slot panel, a booking, a preview row — which wants the
 * weekday named even when the brand's pattern is `DD/MM/YYYY`. Prefixed with
 * this locale's weekday name unless the pattern already carries one, so a brand
 * that chose `dddd D MMMM YYYY` is not told the weekday twice.
 */
export function formatDateWithWeekday(
  value: string | null | undefined,
  locale: Locale,
  formats: DateTimeFormats,
  weekday: 'long' | 'short',
  timeZone: string = browserTimeZone(),
): string {
  const parts = partsOf(value, timeZone);
  if (parts === null) return NO_INSTANT;

  const names = patternNamesFor(locale);
  const day = formatDatePattern(formats.dateFormat, parts, names);
  if (namesWeekday(formats.dateFormat)) return day;

  const name = (weekday === 'long' ? names.weekdays : names.weekdaysShort)[parts.weekday] ?? '';
  return `${name}${JOINER[locale]}${day}`;
}

/** A time of day alone — a slot, an appointment hour — under `brand.timeFormat`. */
export function formatClock(
  value: string | null | undefined,
  locale: Locale,
  formats: DateTimeFormats,
  timeZone: string = browserTimeZone(),
): string {
  const parts = partsOf(value, timeZone);
  if (parts === null) return NO_INSTANT;

  return renderClock(parts, formats.timeFormat, meridiemFor(locale));
}

/**
 * Two clocks with a dash between them — a slot's window.
 *
 * Both ends carry their own period on a 12-hour clock: `Intl.formatRange`
 * merges a shared marker ("9:00 – 10:30 AM"), and the operator reads the first
 * end without one and wonders (P25b2).
 */
export function formatClockRange(
  startsAt: string | null | undefined,
  endsAt: string | null | undefined,
  locale: Locale,
  formats: DateTimeFormats,
  timeZone: string = browserTimeZone(),
): string {
  const start = formatClock(startsAt, locale, formats, timeZone);
  const end = formatClock(endsAt, locale, formats, timeZone);
  if (start === NO_INSTANT || end === NO_INSTANT) return NO_INSTANT;

  return `${start}${RANGE_DASH}${end}`;
}

/**
 * A day **and** a time — the shape an event needs.
 *
 * A probe, a config write and a sign-in are instants: "9 August" for something
 * that happened twenty minutes ago reads as old news, and two probes on the same
 * day become indistinguishable.
 */
export function formatDateTime(
  value: string | null | undefined,
  locale: Locale,
  formats: DateTimeFormats,
  timeZone: string = browserTimeZone(),
): string {
  const parts = partsOf(value, timeZone);
  if (parts === null) return NO_INSTANT;

  const day = formatDatePattern(formats.dateFormat, parts, patternNamesFor(locale));
  const clock = renderClock(parts, formats.timeFormat, meridiemFor(locale));

  return `${day}${JOINER[locale]}${clock}`;
}
